import React, { useEffect, useState, useCallback } from "react";
import { toast } from "./ui/sonner";
import { Loader2, Save, Bot, Sparkles, FileText, ChevronDown, ChevronUp, X, MapPin } from "lucide-react";
import SharedResources from "./SharedResources";
import { AutoGrowTextarea } from "./AutoGrowTextarea";
import { INTAKE_GROUPS, INTAKE_SCHEMA, fieldFilled, RoomsField, SizeSlider, MultiChips, AduTypesField, MultiCustom } from "./intakeSchema";
import { useAiModels } from "../lib/aiModels";

// 设计部镜像不展示竞品询价相关字段
const MIRROR_SCHEMA = INTAKE_SCHEMA.filter((f) => f.key !== "competitor_quote");

const inputCls = "w-full text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 focus:border-indigo-400 focus:outline-none";

export default function SurveyIntakeMirror({ api, leadId, zh, canEdit = true }) {
  const aiModels = useAiModels();
  const [lead, setLead] = useState(null);
  const [top, setTop] = useState({});
  const [intake, setIntake] = useState({});
  const [options, setOptions] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [sumBusy, setSumBusy] = useState(false);

  const hydrate = useCallback((l) => {
    const t = {}; const it = { ...(l.intake || {}) };
    INTAKE_SCHEMA.forEach((f) => { if (f.top) t[f.key] = l[f.key] ?? ""; });
    setTop(t); setIntake(it); setDirty(false); setSummary(l.intakeSummary || null);
  }, []);
  const loadLead = useCallback(async () => {
    try { const { data } = await api.get(`/crm/leads/${leadId}`); setLead(data); hydrate(data); }
    catch { /* */ }
  }, [api, leadId, hydrate]);
  useEffect(() => { loadLead(); api.get("/crm/settings/intake-options").then(({ data }) => setOptions(data.options || {})).catch(() => {}); }, [loadLead]); // eslint-disable-line

  const setF = (f, v) => { setDirty(true); if (f.top) setTop((s) => ({ ...s, [f.key]: v })); else setIntake((s) => ({ ...s, [f.key]: v })); };
  const setIntakeKey = (k, v) => { setDirty(true); setIntake((s) => ({ ...s, [k]: v })); };
  const setTopKey = (k, v) => { setDirty(true); setTop((s) => ({ ...s, [k]: v })); };
  const val = (f) => (f.top ? top[f.key] : intake[f.key]);

  const filledCount = MIRROR_SCHEMA.reduce((n, f) => n + (fieldFilled(f, top, intake) ? 1 : 0), 0);
  const total = MIRROR_SCHEMA.length;
  const pct = Math.round((filledCount / total) * 100);

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/crm/leads/${leadId}/survey-intake`, { top, intake }); setLead(data); hydrate(data); toast.success(zh ? "现场调研已保存（销售端实时同步）" : "Saved (synced to sales)"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    setBusy(false);
  };
  const aiFill = async () => {
    if (!aiText.trim()) { toast.error(zh ? "请先输入描述" : "Enter a description"); return; }
    setAiBusy(true);
    try {
      const schema = MIRROR_SCHEMA.map((f) => ({ key: f.key, type: f.type, top: !!f.top, label: f.label, keys: (f.keys || []).map((x) => x.k), options: f.options || options[f.key] || [] }));
      const { data } = await api.post(`/crm/leads/${leadId}/intake-ai`, { text: aiText, schema });
      INTAKE_SCHEMA.filter((f) => f.type === "wan").forEach((f) => {
        const bag = f.top ? data.top : data.intake;
        if (bag && bag[f.key] != null && bag[f.key] !== "") bag[f.key] = Math.round(Number(bag[f.key]) * 10000);
      });
      if (data.top) setTop((s) => ({ ...s, ...data.top }));
      if (data.intake) setIntake((s) => ({ ...s, ...data.intake }));
      setDirty(true);
      toast.success(zh ? `已填充 ${data.filled || 0} 项，请核对后保存` : `Filled ${data.filled || 0} — review then save`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 生成失败" : "AI failed")); }
    setAiBusy(false);
  };
  const genSummary = async () => {
    setSumBusy(true);
    try { const { data } = await api.post(`/crm/leads/${leadId}/intake-summary`); setSummary(data); toast.success(zh ? "AI 摘要已生成并存档" : "Summary saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 生成失败" : "AI failed")); }
    setSumBusy(false);
  };
  const exportPdf = async () => {
    try { const res = await api.get(`/crm/leads/${leadId}/intake-pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error(zh ? "导出失败" : "Export failed"); }
  };

  const renderField = (f) => {
    const opts = f.options || options[f.key] || [];
    if (f.type === "select") {
      return (
        <select data-testid={`dsurvey-${leadId}-${f.key}`} disabled={!canEdit} value={val(f) || ""} onChange={(e) => setF(f, e.target.value)} className={inputCls}>
          <option value="">{zh ? "请选择" : "Select"}</option>
          {opts.map((o) => <option key={o} value={o}>{f.optionLabel ? f.optionLabel(o, zh) : o}</option>)}
        </select>
      );
    }
    if (f.type === "multi") {
      return <MultiChips opts={opts} value={val(f)} onChange={(v) => setF(f, v)} canEdit={canEdit} noneOpt={f.noneOpt} testId={`dsurvey-${leadId}-${f.key}`} />;
    }
    if (f.type === "multicustom") {
      return <MultiCustom opts={f.options || []} value={val(f)} onChange={(v) => setF(f, v)} canEdit={canEdit} zh={zh} testId={`dsurvey-${leadId}-${f.key}`} />;
    }
    if (f.type === "rooms") {
      return <RoomsField f={f} intake={intake} setIntakeKey={setIntakeKey} canEdit={canEdit} zh={zh} testId={`dsurvey-${leadId}-${f.key}`} />;
    }
    if (f.type === "adutypes") {
      return <AduTypesField top={top} intake={intake} setIntakeKey={setIntakeKey} setTopKey={setTopKey} canEdit={canEdit} zh={zh} testId={`dsurvey-${leadId}-${f.key}`} />;
    }
    if (f.type === "sizeslider") {
      return <SizeSlider f={f} value={val(f)} onChange={(v) => setF(f, v)} canEdit={canEdit} testId={`dsurvey-${leadId}-${f.key}`} />;
    }
    if (f.type === "wan") {
      const dollars = val(f);
      const shown = (dollars === "" || dollars == null) ? "" : Number(dollars) / 10000;
      return (
        <div className="relative">
          <input data-testid={`dsurvey-${leadId}-${f.key}`} disabled={!canEdit} type="number" value={shown} onChange={(e) => setF(f, e.target.value === "" ? "" : Math.round(Number(e.target.value) * 10000))} className={`${inputCls} pr-10`} />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">{zh ? "万元" : "10k"}</span>
        </div>
      );
    }
    if (f.type === "date") {
      return <input data-testid={`dsurvey-${leadId}-${f.key}`} disabled={!canEdit} type="date" value={val(f) || ""} onChange={(e) => setF(f, e.target.value)} className={inputCls} />;
    }
    if (f.key === "primaryConcern") {
      return <AutoGrowTextarea data-testid={`dsurvey-${leadId}-${f.key}`} disabled={!canEdit} value={val(f) ?? ""} onChange={(e) => setF(f, e.target.value)} className={inputCls} />;
    }
    return <input data-testid={`dsurvey-${leadId}-${f.key}`} disabled={!canEdit} type={f.type === "number" ? "number" : "text"} value={val(f) ?? ""} onChange={(e) => setF(f, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} className={inputCls} />;
  };

  if (!lead) return <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-[12px] text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" />{zh ? "加载现场调研…" : "Loading…"}</div>;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-3" data-testid={`dsurvey-${leadId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-[160px] cursor-pointer group" data-testid={`dsurvey-progress-toggle-${leadId}`} onClick={() => setExpanded((v) => !v)}>
          <div className="flex items-center justify-between text-[11px] text-amber-800 mb-1">
            {/* 2026-10-20 修正：标题原先写死「可编辑」，但 canEdit=false 时字段其实是禁用的
                （签约前营销方案工单详情就是用只读镜像），文案与实际行为矛盾，
                会让人以为改不动是 bug。这里按 canEdit 切换文案。 */}
            <span className="inline-flex items-center gap-1 font-semibold">{expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}<MapPin className="h-3.5 w-3.5" />{zh ? (canEdit ? "概览 · 现场调研（可编辑，实时同步销售）" : "概览 · 现场调研（只读，由销售维护）") : "Overview · Site Intake"} · {filledCount}/{total}</span>
            <span className="font-semibold text-indigo-600" data-testid={`dsurvey-pct-${leadId}`}>{pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-testid={`dsurvey-progress-bar-${leadId}`}><div className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-indigo-600"}`} style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={exportPdf} data-testid={`dsurvey-pdf-${leadId}`} className="inline-flex items-center gap-1 text-[11px] text-indigo-600 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-50"><FileText className="h-3 w-3" />{zh ? "调研表PDF" : "PDF"}</button>
          <button onClick={genSummary} disabled={sumBusy} data-testid={`dsurvey-summary-${leadId}`} className="inline-flex items-center gap-1 text-[11px] text-indigo-600 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-50 disabled:opacity-50">{sumBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bot className="h-3 w-3" />}{zh ? "AI 摘要" : "AI Summary"}</button>
          {canEdit && <button onClick={save} disabled={busy || !dirty} data-testid={`dsurvey-save-${leadId}`} className="inline-flex items-center gap-1 text-[11px] text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2.5 py-1 disabled:opacity-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}{zh ? "保存调研" : "Save"}</button>}
        </div>
      </div>

      {summary && (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-2 space-y-1" data-testid={`dsurvey-summary-box-${leadId}`}>
          <div className="flex items-center justify-between"><span className="text-[11px] font-semibold text-indigo-600 inline-flex items-center gap-1"><Bot className="h-3 w-3" />{zh ? "AI 可行性与风险摘要" : "AI feasibility & risk"}</span><button onClick={() => setSummary(null)} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button></div>
          {summary.feasibility && <p className="text-[12px] text-slate-700"><span className="font-semibold text-emerald-600">{zh ? "可行性 " : "Feasibility "}</span>{summary.feasibility}</p>}
          {(summary.risks || []).length > 0 && <ul className="list-disc pl-5 text-[12px] text-slate-700"><span className="font-semibold text-amber-600">{zh ? "风险" : "Risks"}</span>{summary.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>}
          {summary.recommendation && <p className="text-[12px] text-slate-700"><span className="font-semibold text-indigo-600">{zh ? "建议 " : "Recommendation "}</span>{summary.recommendation}</p>}
        </div>
      )}

      <SharedResources api={api} leadId={leadId} zh={zh} canEdit={canEdit} />

      {expanded && (
        <>
          {canEdit && (
            <div className="rounded-lg border border-indigo-100 bg-white p-2 space-y-1.5" data-testid={`dsurvey-ai-panel-${leadId}`}>
              <div className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600"><Sparkles className="h-3 w-3" />{zh ? `文字描述智能填表 (${aiModels.extract || "AI"})` : "AI text-to-form"}</div>
              <textarea data-testid={`dsurvey-ai-text-${leadId}`} value={aiText} onChange={(e) => setAiText(e.target.value)} placeholder={zh ? "例如：主屋约1800平呎，1990年建，现有3室2卫，想加建1B1B独立ADU出租，预算30万…" : "Describe the site & needs…"} className="w-full text-[12px] rounded-lg border border-slate-200 px-2 py-1.5 min-h-[52px] focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              <button onClick={aiFill} disabled={aiBusy} data-testid={`dsurvey-ai-fill-${leadId}`} className="inline-flex items-center gap-1 text-[11px] text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2.5 py-1 disabled:opacity-50">{aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}{zh ? "智能填表" : "Fill"}</button>
            </div>
          )}
          {INTAKE_GROUPS.map((g) => (
            <div key={g.id} className="space-y-2" data-testid={`dsurvey-group-${leadId}-${g.id}`}>
              <p className="text-[11px] font-semibold text-amber-700 border-b border-amber-100 pb-1">{zh ? g.zh : g.en}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
                {MIRROR_SCHEMA.filter((f) => f.group === g.id).map((f) => (
                  <div key={f.key} className={["multi", "rooms", "adutypes", "multicustom"].includes(f.type) ? "sm:col-span-2 lg:col-span-3" : ""}>
                    <label className="text-[11px] text-slate-500 block mb-0.5">{zh ? f.label[0] : f.label[1]}</label>
                    {renderField(f)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
